import { QueryKey, useQuery } from "@tanstack/react-query";
import { Schema, Either } from "effect";
import { TreeFormatter } from "effect/ParseResult";
import { SanityImportLastExecutionSchema } from "@silversea-ssc/dotcom-api-types/SanityImportLastExecutionSchema";
import { SanityImportRunSchema } from "@silversea-ssc/dotcom-api-types/SanityImportRunSchema";
import { getSanityAuthToken } from "./getSanityAuthToken";

type FetchArgs = {
  sanityImportApiUrl: string;
  action: string;
  queryParams?: Record<string, string>;
  headers?: Record<string, string>;
};
export class HttpError extends Error {
  constructor(
    public status: number,
    message: string
  ) {
    super(message);
    this.name = "HttpError";
  }
}

const fetchFun = async <T>(
  url: string,
  action: string,
  headers?: Record<string, string>
): Promise<T> => {
  const token = getSanityAuthToken();

  if (!token) {
    throw new HttpError(
      401,
      "Authentication required. Please log in to Sanity Studio."
    );
  }

  const response = await fetch(url, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...headers
    },
    method: action === "sanity-import/start" ? "POST" : "GET"
  });

  if (!response.ok) {
    throw new HttpError(
      response.status,
      `HTTP ${response.status}: ${response.statusText}`
    );
  }
  return await response.json();
};

const fetchSanityImport = async <T>({
  sanityImportApiUrl,
  action,
  queryParams,
  headers
}: FetchArgs): Promise<T> => {
  const url = new URL(action, sanityImportApiUrl);

  if (queryParams) {
    Object.entries(queryParams).forEach(([key, value]) => {
      url.searchParams.append(key, value);
    });
  }

  return fetchFun<T>(url.toString(), action, headers);
};

type QueryArgs<A, I> = {
  queryKey: QueryKey;
  enabled: boolean;
  action: string;
  sanityImportApiUrl: string;
  type: Schema.Schema<A, I>;
  queryParams?: Record<string, string>;
  headers?: Record<string, string>;
  refetchInterval?: number;
};

const useSanityImportQuery = <A, I>(props: QueryArgs<A, I>) => {
  const {
    queryKey,
    enabled,
    action,
    sanityImportApiUrl,
    type,
    queryParams,
    headers,
    refetchInterval
  } = props;

  return useQuery({
    queryKey: queryKey,
    enabled: enabled,
    retry: (failureCount, error) => {
      if (
        error instanceof HttpError &&
        (error.status === 401 || error.status === 400 || error.status === 403)
      ) {
        return false;
      }
      return failureCount < 3;
    },
    refetchOnWindowFocus: false,
    refetchInterval: (query) => {
      const error = query.state.error;

      if (error instanceof HttpError) {
        if (
          error.status === 401 ||
          error.status === 400 ||
          error.status === 403
        ) {
          console.log("Stopping refetch due to auth error");
          return false;
        }
      }
      return refetchInterval ?? false;
    },

    queryFn: () =>
      fetchSanityImport<I>({
        sanityImportApiUrl,
        action,
        queryParams,
        headers
      }).then((data) =>
        Schema.decodeEither(type)(data).pipe(
          Either.match({
            onLeft: (error) => {
              throw new Error(
                `Decode error in ${action}: ${TreeFormatter.formatErrorSync(error)}`
              );
            },
            onRight: (a) => a
          })
        )
      )
  });
};

export const useSanityImportApi = {
  useLastExecution: (sanityImportApiUrl: string, enabled: boolean = true) =>
    useSanityImportQuery({
      queryKey: ["sanity-import", "last-execution"],
      enabled,
      action: "sanity-import/last-execution",
      sanityImportApiUrl,
      type: SanityImportLastExecutionSchema,
      refetchInterval: 5000
    }),

  useRunImport: (sanityImportApiUrl: string, importType?: string) =>
    useSanityImportQuery({
      queryKey: ["sanity-import", "run", importType],
      enabled: !!importType,
      action: "sanity-import/start",
      sanityImportApiUrl,
      type: SanityImportRunSchema,
      queryParams: importType ? { type: importType } : undefined
    })
};
